import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { useMediaQuery } from '../hooks/use-media-query';
import {
  House,
  ChartLineUp,
  Brain,
  Microphone,
  MapPin,
  AppWindow,
  Plug,
  Bell,
  Gear,
  SignOut,
  CaretDown,
  ChartBar,
  SlidersHorizontal,
  Gauge,
  LinkSimple,
  User,
  List,
  X,
  CaretRight,
  Calendar,
  Lifebuoy
} from '@phosphor-icons/react';
import { isRouteAccessible } from '../config/routeAccess';
import { motion, AnimatePresence } from 'framer-motion';

// Elegant animation variants
const fadeSlide = {
  hidden: { opacity: 0, y: -8 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
  },
  exit: { 
    opacity: 0, 
    y: -4,
    transition: { duration: 0.2 }
  }
};

const scaleIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: { 
    scale: 1, 
    opacity: 1,
    transition: { 
      type: "spring", 
      damping: 30, 
      stiffness: 400,
      duration: 0.5 
    }
  },
  exit: { 
    scale: 0.98, 
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

// Ethereal Gradient Component - Living AI Consciousness
const EtherealAvatar = ({ children, size = "w-12 h-12" }) => {
  // Generate unique random values once per component instance (stable across re-renders)
  const animationConfig = React.useMemo(() => ({
    flow1Duration: 25 + Math.random() * 15, // 25-40s
    flow2Duration: 20 + Math.random() * 15, // 20-35s
    flow3Duration: 30 + Math.random() * 20, // 30-50s
    rippleDuration: 15 + Math.random() * 10, // 15-25s

    delay1: Math.random() * 20,
    delay2: Math.random() * 15,
    delay3: Math.random() * 25,
    rippleDelay: Math.random() * 10,
  }), []); // Empty dependency array ensures this only runs once per component instance

  return (
    <div className={`${size} rounded-2xl relative overflow-hidden flex items-center justify-center border border-white/30 shadow-sm`}>
      {/* Base flowing layer - more diffused */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 120% 80% at 30% 20%, rgba(59, 130, 246, 0.7) 0%, transparent 70%),
            radial-gradient(ellipse 100% 120% at 70% 60%, rgba(236, 72, 153, 0.6) 0%, transparent 65%),
            radial-gradient(ellipse 140% 90% at 20% 80%, rgba(255, 235, 59, 0.6) 0%, transparent 75%)
          `,
          animation: `organicFlow1 ${animationConfig.flow1Duration}s ease-in-out infinite`,
          animationDelay: `-${animationConfig.delay1}s`,
          filter: 'blur(3px)'
        }}
      />

      {/* Ripple layer - larger, more diffused */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 150% 100% at 60% 40%, rgba(236, 72, 153, 0.5) 0%, transparent 60%),
            radial-gradient(ellipse 130% 110% at 40% 70%, rgba(59, 130, 246, 0.5) 0%, transparent 65%)
          `,
          animation: `organicFlow2 ${animationConfig.flow2Duration}s ease-in-out infinite`,
          animationDelay: `-${animationConfig.delay2}s`,
          filter: 'blur(4px)'
        }}
      />

      {/* Deep current layer - very diffused */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 180% 120% at 50% 50%, rgba(255, 235, 59, 0.4) 0%, transparent 80%),
            radial-gradient(ellipse 160% 140% at 80% 30%, rgba(59, 130, 246, 0.3) 0%, transparent 75%)
          `,
          animation: `organicFlow3 ${animationConfig.flow3Duration}s ease-in-out infinite`,
          animationDelay: `-${animationConfig.delay3}s`,
          filter: 'blur(5px)'
        }}
      />

      {/* Surface ripples - very subtle */}
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          background: `radial-gradient(ellipse 200% 150% at 45% 55%, rgba(255, 255, 255, 0.4) 0%, transparent 50%)`,
          animation: `surfaceRipple ${animationConfig.rippleDuration}s ease-in-out infinite`,
          animationDelay: `-${animationConfig.rippleDelay}s`,
          filter: 'blur(6px)'
        }}
      />

      <div className="relative z-10 text-white font-medium">
        {children}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes organicFlow1 {
            0%, 100% { 
              transform: scale(1) rotate(0deg) translate(0%, 0%);
              opacity: 0.8;
            }
            25% { 
              transform: scale(1.2) rotate(90deg) translate(15%, -10%);
              opacity: 0.6;
            }
            50% { 
              transform: scale(0.95) rotate(180deg) translate(-8%, 20%);
              opacity: 0.9;
            }
            75% { 
              transform: scale(1.1) rotate(270deg) translate(-15%, -8%);
              opacity: 0.5;
            }
          }

          @keyframes organicFlow2 {
            0%, 100% { 
              transform: scale(1.05) rotate(45deg) translate(8%, 4%);
              opacity: 0.7;
            }
            33% { 
              transform: scale(0.9) rotate(135deg) translate(-18%, 15%);
              opacity: 0.9;
            }
            66% { 
              transform: scale(1.25) rotate(225deg) translate(12%, -22%);
              opacity: 0.4;
            }
          }

          @keyframes organicFlow3 {
            0%, 100% { 
              transform: scale(0.8) rotate(0deg) translate(-4%, -4%);
              opacity: 0.6;
            }
            20% { 
              transform: scale(1.3) rotate(72deg) translate(22%, 8%);
              opacity: 0.3;
            }
            40% { 
              transform: scale(1.0) rotate(144deg) translate(-15%, 25%);
              opacity: 0.8;
            }
            60% { 
              transform: scale(1.2) rotate(216deg) translate(18%, -18%);
              opacity: 0.5;
            }
            80% { 
              transform: scale(0.95) rotate(288deg) translate(-22%, 12%);
              opacity: 0.7;
            }
          }

          @keyframes surfaceRipple {
            0%, 100% { 
              transform: scale(0.7) translate(0%, 0%);
              opacity: 0.3;
            }
            25% { 
              transform: scale(1.8) translate(30%, -15%);
              opacity: 0.1;
            }
            50% { 
              transform: scale(1.3) translate(-22%, 25%);
              opacity: 0.2;
            }
            75% { 
              transform: scale(2.0) translate(15%, -30%);
              opacity: 0.05;
            }
          }
        `
      }} />
    </div>
  );
};

const MobileMenu = ({
  show,
  onClose,
  navItems,
  userData,
  accountData,
  businessTypeFriendlyName,
  handleLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show, onClose]);

  const getInitials = () => {
    const displayName = userData?.user_metadata?.full_name || accountData?.full_name;
    if (displayName) {
      return displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return userData?.email?.charAt(0).toUpperCase() || 'U';
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(item => location.pathname === item.path);
  };

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            ref={mobileMenuRef}
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ 
              type: "spring", 
              damping: 30, 
              stiffness: 300,
              duration: 0.5 
            }}
            className="fixed left-0 top-0 h-full w-80 bg-white/95 backdrop-blur-xl shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-6 border-b border-gray-100/50">
              <div className="flex items-center justify-between mb-4">
                <EtherealAvatar>
                  <span className="text-lg font-medium">{getInitials()}</span>
                </EtherealAvatar>
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </motion.button>
                </div>
                <div>
                <div className="text-lg font-medium text-gray-900">
                    {accountData?.full_name || userData?.email?.split('@')[0] || 'User'}
                  </div>
                <div className="text-sm text-gray-500 mt-0.5">
                    {accountData?.company_name || businessTypeFriendlyName}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    transition: { delay: index * 0.1, duration: 0.4 }
                  }}
                >
                  {item.submenu ? (
                    <div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setExpandedSection(expandedSection === item.label ? null : item.label)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-left font-medium transition-all ${
                          isSubmenuActive(item.submenu)
                            ? 'bg-black text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <item.icon size={20} />
                          <span>{item.label}</span>
                        </div>
                        <CaretRight 
                          className={`w-5 h-5 transition-transform ${
                            expandedSection === item.label ? 'rotate-90' : ''
                          }`} 
                        />
                      </motion.button>

                      <AnimatePresence>
                        {expandedSection === item.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden ml-4 mt-1 space-y-1"
                          >
                            {item.submenu.map((subItem) => (
                              <motion.button
                                key={subItem.path}
                                whileHover={{ scale: 1.02, x: 4 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleNavClick(subItem.path)}
                                className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                  location.pathname === subItem.path
                                    ? 'bg-black text-white'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                <subItem.icon size={16} />
                                <span>{subItem.label}</span>
                              </motion.button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleNavClick(item.path)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-left font-medium transition-all ${
                        location.pathname === item.path
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </motion.button>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-4 border-t border-gray-100/50 space-y-1">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleNavClick('/account/general')}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
              >
                <Gear size={18} />
                <span>Settings</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleNavClick('/help')}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-all"
              >
                <Lifebuoy size={18} />
                <span>Help</span>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
              >
                <SignOut size={18} />
                <span>Sign out</span>
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const TopNav = () => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1000px)');

  const [userData, setUserData] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [businessTypeFriendlyName, setBusinessTypeFriendlyName] = useState('');
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const profileMenuRef = useRef(null);

  // Define navigation structure
  const navItems = [
    {
      icon: House,
      label: 'Home',
      path: '/home',
    },
    {
      icon: Gauge,
      label: 'Activity',
      path: '/activity',
    },
    {
      icon: SlidersHorizontal,
      label: 'Train',
      submenu: [
        { label: 'Brain', path: '/train/brainv2', icon: Brain },
        { label: 'Places', path: '/train/places', icon: MapPin },
        ...(businessType === 'property_management' ? [
          { label: 'Tour Times', path: '/property-management/tour-times', icon: Calendar }
        ] : [])
      ],
    },
    {
      icon: LinkSimple,
      label: 'Connect',
      submenu: [
        { label: 'Setup', path: '/connect/setup', icon: Plug },
        { label: 'Alerts', path: '/connect/alerts', icon: Bell },
      ],
    },
  ].map((item) => {
    if (item.submenu) {
      return {
        ...item,
        submenu: item.submenu.filter((subItem) =>
          isRouteAccessible(businessType, subItem.path)
        ),
      };
    }
    return item;
  }).filter((item) =>
    item.path
      ? isRouteAccessible(businessType, item.path)
      : item.submenu?.length > 0
  );

  useEffect(() => {
    fetchUserData();

    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
        setActiveSubmenu(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId, error: accountIdError } = await getUserAccountId();
        if (accountIdError || !accountId) {
          throw new Error(accountIdError || 'Account ID not found');
        }

        const [accountResponse, businessTypeResponse] = await Promise.all([
          supabase
            .from('accounts')
            .select('*')
            .eq('id', accountId)
            .maybeSingle(),
          supabase
            .from('system_business_types')
            .select('friendly_name')
            .eq('name', businessType)
            .eq('active', true)
            .maybeSingle()
        ]);

        if (accountResponse.error) {
          console.error('Error fetching account data:', accountResponse.error);
        }

        if (businessTypeResponse.error) {
          console.error('Error fetching business type:', businessTypeResponse.error);
        }

        setUserData(session.user);
        setAccountData(accountResponse.data);
        setBusinessTypeFriendlyName(businessTypeResponse.data?.friendly_name || 'Business');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getInitials = () => {
    const displayName = userData?.user_metadata?.full_name || accountData?.full_name;
    if (displayName) {
      return displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return userData?.email?.charAt(0).toUpperCase() || 'U';
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(item => location.pathname === item.path);
  };

  // Loading state
  if (isLoading) {
    const SkeletonPulse = ({ className }) => (
      <motion.div
        animate={{ opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        className={`bg-gray-200 rounded-xl ${className}`}
      />
    );

    return (
      <div className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        {isMobile ? (
          // Mobile skeleton
          <div className="flex items-center justify-between h-20 px-6">
            {/* Mobile menu button skeleton */}
            <SkeletonPulse className="w-11 h-11" />

            {/* Logo skeleton */}
            <div className="flex flex-col items-center space-y-1">
              <SkeletonPulse className="w-24 h-5" />
              <SkeletonPulse className="w-16 h-3" />
            </div>

            {/* Profile avatar skeleton */}
            <SkeletonPulse className="w-11 h-11 rounded-2xl" />
          </div>
        ) : (
          // Desktop skeleton
          <div className="flex items-center justify-between h-20 px-8">
            {/* Logo skeleton */}
            <div className="flex flex-col items-start space-y-1">
              <SkeletonPulse className="w-32 h-6" />
              <SkeletonPulse className="w-20 h-3" />
            </div>

            {/* Navigation items skeleton */}
            <div className="flex items-center space-x-2">
              <SkeletonPulse className="w-16 h-9" />
              <SkeletonPulse className="w-20 h-9" />
              <SkeletonPulse className="w-14 h-9" />
              <SkeletonPulse className="w-18 h-9" />
            </div>

            {/* Profile section skeleton */}
            <div className="flex items-center space-x-3">
              <SkeletonPulse className="w-10 h-10 rounded-2xl" />
              <div className="hidden sm:block space-y-1">
                <SkeletonPulse className="w-24 h-4" />
                <SkeletonPulse className="w-20 h-3" />
              </div>
              <SkeletonPulse className="w-4 h-4" />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeSlide}
          className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100"
        >
          <div className="flex items-center justify-between h-20 px-6">
            {/* Mobile Menu Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowMobileMenu(true)}
              className="w-11 h-11 rounded-xl flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
            >
              <List size={22} />
            </motion.button>

            {/* Logo */}
            <motion.button 
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/home')}
              className="flex flex-col items-center"
            >
              <span className="text-xl font-bold text-gray-900">The Savants</span>
              <span className="text-xs text-gray-500 font-medium -mt-1">
                for {businessTypeFriendlyName}
              </span>
            </motion.button>

            {/* Profile Avatar */}
            <EtherealAvatar size="w-11 h-11">
              <span className="text-sm font-medium">{getInitials()}</span>
            </EtherealAvatar>
          </div>
        </motion.div>

        <MobileMenu 
          show={showMobileMenu}
          onClose={() => setShowMobileMenu(false)}
          navItems={navItems}
          userData={userData}
          accountData={accountData}
          businessTypeFriendlyName={businessTypeFriendlyName}
          handleLogout={handleLogout}
        />
      </>
    );
  }

  // Desktop version
  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={fadeSlide}
      className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100"
    >
      <div className="flex items-center justify-between h-20 px-8">
        {/* Logo */}
        <motion.button 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/home')}
          className="flex flex-col items-start"
        >
          <span className="text-2xl font-bold text-gray-900">The Savants</span>
          <span className="text-xs text-gray-500 font-medium -mt-1">
              for {businessTypeFriendlyName}
            </span>
        </motion.button>

        {/* Navigation Items */}
        <div className="flex items-center space-x-2">
          {navItems.map((item, index) => (
            <motion.div 
              key={item.label} 
              className="relative"
              initial={{ opacity: 0, y: -10 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: { delay: index * 0.1, duration: 0.4 }
              }}
            >
              {item.submenu ? (
                <div className="group">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isSubmenuActive(item.submenu)
                        ? 'bg-black text-white shadow-lg'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                    <CaretDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                  </motion.button>

                  {/* Dropdown Menu */}
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100/50 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    {item.submenu.map((subItem, subIndex) => (
                      <motion.div
                        key={subItem.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ 
                          opacity: 1, 
                          x: 0,
                          transition: { delay: subIndex * 0.05, duration: 0.3 }
                        }}
                      >
                        <NavLink
                        to={subItem.path}
                        className={({ isActive }) => `
                            flex items-center space-x-3 px-4 py-3 mx-2 rounded-xl text-sm font-medium transition-all
                          ${isActive 
                              ? 'bg-black text-white' 
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }
                        `}
                      >
                          <subItem.icon size={16} />
                        <span>{subItem.label}</span>
                      </NavLink>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `
                    flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-black text-white shadow-lg' 
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                >
                  {({ isActive }) => (
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex items-center space-x-2"
                    >
                      <item.icon size={18} />
                  <span>{item.label}</span>
                    </motion.div>
                  )}
                </NavLink>
              )}
            </motion.div>
          ))}
        </div>

        {/* Profile Section */}
        <div className="relative" ref={profileMenuRef}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-gray-100 transition-all group"
          >
            <EtherealAvatar size="w-10 h-10">
              <span className="text-sm font-medium">{getInitials()}</span>
            </EtherealAvatar>
            <div className="text-left hidden sm:block">
              <div className="text-sm font-medium text-gray-900">
                {userData?.user_metadata?.full_name || accountData?.full_name || userData?.email?.split('@')[0] || 'User'}
              </div>
              <div className="text-xs text-gray-500">
                {accountData?.company_name || businessTypeFriendlyName}
              </div>
            </div>
            <CaretDown className={`w-4 h-4 text-gray-400 transition-transform ${
              showProfileMenu ? 'rotate-180' : ''
            }`} />
          </motion.button>

          {/* Profile Dropdown */}
          <AnimatePresence>
            {showProfileMenu && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={scaleIn}
                className="absolute right-0 top-full mt-2 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-100/50 overflow-hidden z-50"
              >
                <div className="p-6 border-b border-gray-100/50">
                  <div className="flex items-center space-x-4">
                    <EtherealAvatar size="w-14 h-14">
                      <span className="text-lg font-medium">{getInitials()}</span>
                    </EtherealAvatar>
                    <div>
                      <div className="text-lg font-medium text-gray-900">
                        {userData?.user_metadata?.full_name || accountData?.full_name || userData?.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {accountData?.company_name || businessTypeFriendlyName}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-2">
                  <NavLink
                    to="/account/general"
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <Gear size={18} />
                    <span>Settings</span>
                  </NavLink>

                  <NavLink
                    to="/help"
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <Lifebuoy size={18} />
                    <span>Help</span>
                  </NavLink>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
                  >
                    <SignOut size={18} />
                    <span>Sign out</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default TopNav;